package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.appcontent.withoutNikud
import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.common.ChaptersRepository.Companion.migrateFilename
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimUseCase
import com.eladfinish.bible.contest.common.usecases.VersePickTarget
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.features.hebrewCalendar.ParashaCalendar
import com.eladfinish.bible.contest.screens.lexicographicalSort.GetVerseCountUseCase
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.ExtractMikudListUseCase
import com.eladfinish.bible.contest.screens.search.usecases.PickVerseFromChapterUseCaseV2
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.addGershaimAndColon
import com.eladfinish.bible.contest.utils.replaceKtivWithQriNew
import com.eladfinish.bible.contest.utils.toOtiot
import com.russhwolf.settings.Settings
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlin.random.Random
import kotlin.time.Instant

/**
 * The verse the "rotating verse" widget shows, as a pure function of the [VerseWidgetConfig] and the
 * time slot (see [VerseWidgetSlots]): the same inputs give the same verse on every platform and every
 * run, so a late widget update still shows the right verse, and the iOS widget can be handed a
 * whole schedule in advance.
 *
 * The number of verses of a chapter comes from the app's table of verse counts, so that laying out a
 * rotation over many chapters (the whole Tanakh, a mikud of all of it) does not read a file per chapter.
 *
 * Random picks are drawn from a generator seeded by the config's seed and the slot. A random pick
 * that is too long for a small widget is drawn again, up to [MAX_ATTEMPTS] times. A sequential
 * rotation shows every verse as it is.
 */
class GetWidgetVerseUseCase(
    private val assetReader: AssetReader,
    private val settings: Settings,
    private val mikudRepository: MikudRepository,
    private val extractMikudListUseCase: ExtractMikudListUseCase,
    private val pickVerseFromChapterUseCaseV2: PickVerseFromChapterUseCaseV2,
    private val removeTeamimUseCase: RemoveTeamimUseCase,
    private val getParashaRangeUseCase: GetParashaRangeUseCase,
    private val getVerseCountUseCase: GetVerseCountUseCase,
) {

    /** The verse on screen at [instant]. */
    operator fun invoke(
        config: VerseWidgetConfig,
        instant: Instant,
        timeZone: TimeZone = TimeZone.currentSystemDefault(),
    ): WidgetVerse? =
        session(config).verseAt(VerseWidgetSlots.slotAt(instant, config, timeZone))

    /**
     * Resolves many slots of one config, reading each chapter file only once. Use a session
     * to build a schedule.
     */
    fun session(config: VerseWidgetConfig): Session = Session(config)

    inner class Session internal constructor(
        private val config: VerseWidgetConfig,
    ) {

        private val chapterTexts = HashMap<String, String>()
        private val parashaPlans = HashMap<LocalDate, ParashaPlan?>()

        private val mikudBooks: List<List<String>> by lazy { mikudChapterFiles() }
        private val mikudSegments: List<Segment> by lazy { wholeChapters(mikudBooks.flatten()) }
        private val allSegments: List<Segment> by lazy { wholeChapters(allBooks.flatten()) }
        private val bookSegments: List<Segment> by lazy { wholeChapters(bookChapterFiles(config.bookNumber)) }

        // Only the names of the files: nothing is read before a chapter is asked for
        private val allBooks: List<List<String>> by lazy {
            (VerseWidgetConfig.FIRST_BOOK_NUMBER..VerseWidgetConfig.LAST_BOOK_NUMBER).map(::bookChapterFiles)
        }

        fun verseAt(slot: Long): WidgetVerse? =
            when (config.source) {
                VerseWidgetSource.MIKUD -> inChapters(mikudBooks, mikudSegments, slot)
                VerseWidgetSource.ALL -> inChapters(allBooks, allSegments, slot)
                VerseWidgetSource.BOOK -> inSegments(bookSegments, slot, firstSlot = 0)
                VerseWidgetSource.PARASHA -> parashaPlanAt(slot)?.let { inSegments(it.segments, slot, it.firstSlot) }
            }

        // The mikud and the whole Tanakh: chapters in the Tanakh's order, which are laid one after the other
        // when the rotation is sequential, and in a random rotation drawn as the user chose for the random
        // verse: a book, then a chapter, then a verse (or a chapter, then a verse).
        private fun inChapters(books: List<List<String>>, segments: List<Segment>, slot: Long): WidgetVerse? =
            if (config.order == VerseWidgetOrder.SEQUENTIAL) {
                inSegments(segments, slot, firstSlot = 0)
            } else {
                randomIn(books, slot)
            }

        private fun randomIn(books: List<List<String>>, slot: Long): WidgetVerse? {
            val byBook = settings.getString(
                key = Constants.PREF_RANDOM_APPROACH,
                defaultValue = Constants.APPROACH_BY_BOOK,
            ) == Constants.APPROACH_BY_BOOK
            return drawUntilFits(slot) { random ->
                val chapters = if (byBook) {
                    books.getOrNull(random.nextInt(books.size.coerceAtLeast(1)))
                } else {
                    books.flatten()
                }
                val file = chapters?.getOrNull(random.nextInt(chapters.size.coerceAtLeast(1)))
                    ?: return@drawUntilFits null
                val count = verseCount(file)
                if (count <= 0) null else verse(file, random.nextInt(count) + 1)
            }
        }

        // Verses laid one after the other, going round at the end.
        private fun inSegments(segments: List<Segment>, slot: Long, firstSlot: Long): WidgetVerse? {
            val total = segments.sumOf { length(it) }
            if (total <= 0) {
                return null
            }
            return if (config.order == VerseWidgetOrder.SEQUENTIAL) {
                verseAtPosition(segments, (slot - firstSlot).mod(total))
            } else {
                drawUntilFits(slot) { random -> verseAtPosition(segments, random.nextInt(total)) }
            }
        }

        private fun drawUntilFits(slot: Long, draw: (Random) -> WidgetVerse?): WidgetVerse? {
            var last: WidgetVerse? = null
            repeat(MAX_ATTEMPTS) { attempt ->
                val verse = draw(randomFor(slot, attempt)) ?: return@repeat
                if (verse.text.count { it.isLetter() } <= MAX_RANDOM_LETTERS) {
                    return verse
                }
                last = verse
            }
            return last
        }

        private fun randomFor(slot: Long, attempt: Int): Random =
            Random((config.seed.toLong() shl 32) xor (slot * GOLDEN_GAMMA) xor attempt.toLong())

        private fun verseAtPosition(segments: List<Segment>, position: Int): WidgetVerse? {
            var remaining = position
            for (segment in segments) {
                val length = length(segment)
                if (remaining < length) {
                    return verse(segment.file, segment.firstVerse + remaining)
                }
                remaining -= length
            }
            return null
        }

        private fun length(segment: Segment): Int =
            (segment.lastVerse ?: verseCount(segment.file)) - segment.firstVerse + 1

        private fun verse(file: String, verseNumber: Int): WidgetVerse? {
            val bookNumber = ChaptersRepository.getBookNumber(file)
            val chapter = file.substringAfterLast("-").removeSuffix(ChaptersRepository.TXT).toInt()
            // The pick is asked for the verse as written, with each ketiv still followed by its qere, and the
            // qere is chosen here. With onlyQri the picker does it before it splits the verse number from the
            // text, and a verse that begins with a ketiv ("נא מגדיל (מִגְדּוֹל) …", 2 Samuel 22:51 and 24
            // others) loses its number and its first word.
            val picked = pickVerseFromChapterUseCaseV2(
                bookIndex = bookNumber,
                chapterContent = chapterText(file),
                target = VersePickTarget.Nth(verseNumber),
                onlyQri = false,
            ) ?: return null
            return WidgetVerse(
                // The pick wraps the text in gershaim and ends it with a sof pasuk, for the search results
                text = picked.text
                    .removePrefix("\"")
                    .removeSuffix("\"")
                    .removeSuffix(COLON_SPLITTER)
                    .trim()
                    .replaceKtivWithQriNew()
                    .let { if (config.showTeamim && config.showNikud) it else removeTeamimUseCase(it) }
                    .let { if (config.showNikud) it else it.withoutNikud().replace(ZERO_WIDTH_JOINER, "") }
                    .trim()
                    // In gershaim and with the sof pasuk, as the verse is written on the app's other screens
                    .addGershaimAndColon(),
                // Built from the file and the position: the picker reads the numbers from the chapter's heading
                reference = "${BooksRepository.bookNamesHebrew[bookNumber - 1]} " +
                        "${chapter.toOtiot().addGershaim()}, ${verseNumber.toOtiot().addGershaim()}",
                bookNumber = bookNumber,
                chapter = chapter,
                verse = verseNumber,
            )
        }

        // Two things in the chapter text that the picker's heading and verse-number parsing cannot take: the
        // inverted nun of Numbers 10:35-36 and some verses of Tehilim 107, which stands where the verse number
        // should be (the qere replacement removes it too, but only after the split), and the section
        // mark that Tehilim 119 has between its heading and its first verse.
        private fun chapterText(file: String): String =
            chapterTexts.getOrPut(file) {
                assetReader.readFile(StringConstants.ALL_CHAPTERS + assetReader.fileSeparator + file)
                    .replaceRegex(regex = "${StringConstants.NUN_MENUZERET}\\s?", replacement = "")
                    .withoutSectionMarkAfterHeading()
            }

        private fun String.withoutSectionMarkAfterHeading(): String {
            val headingEnd = indexOf('\n') + 1
            if (headingEnd == 0) {
                return this
            }
            val afterHeading = substring(headingEnd).trimStart()
            val markEnd = afterHeading.indexOf('}')
            if (!afterHeading.startsWith("{") || markEnd < 0) {
                return this
            }
            return substring(0, headingEnd) + "\r\n" + afterHeading.substring(markEnd + 1).trimStart()
        }

        private fun verseCount(file: String): Int = getVerseCountUseCase.inChapterFile(file)

        // The chapters of the mikud, grouped by book, in the Tanakh's order
        private fun mikudChapterFiles(): List<List<String>> =
            mikudRepository.mikudList
                .split(MikudRepository.BOOK_SEPARATOR)
                .map { it.trim() }
                .filter { it.isNotEmpty() }
                .map { entry ->
                    val book = entry.substringBefore(MikudRepository.DASH)
                    extractMikudListUseCase(chapterList = entry).map { chapter ->
                        "_$book${MikudRepository.DASH}$chapter${ChaptersRepository.TXT}".migrateFilename()
                    }
                }
                .filter { it.isNotEmpty() }

        private fun parashaPlanAt(slot: Long): ParashaPlan? {
            val date = VerseWidgetSlots.dateOf(slot, config)
            return parashaPlans.getOrPut(date) {
                // A Shabbat of a holiday has no parasha of its own: show the next regular one
                (0..MAX_WEEKS_AHEAD).firstNotNullOfOrNull { week ->
                    val upcoming = ParashaCalendar.upcoming(today = date, week = week)
                    getParashaRangeUseCase(upcoming.name)?.let { range ->
                        ParashaPlan(
                            segments = segmentsOf(range),
                            firstSlot = VerseWidgetSlots.firstSlotOf(
                                date = upcoming.shabbat.minus(DAYS_BEFORE_SHABBAT, DateTimeUnit.DAY),
                                config = config,
                            ),
                        )
                    }
                }
            }
        }

        private fun segmentsOf(range: ParashaRange): List<Segment> =
            (range.chapterStart..range.chapterEnd).map { chapter ->
                Segment(
                    file = chapterFile(range.bookNumber, chapter),
                    firstVerse = if (chapter == range.chapterStart) range.verseStart else 1,
                    lastVerse = if (chapter == range.chapterEnd) range.verseEnd else null,
                )
            }

        private fun bookChapterFiles(bookNumber: Int): List<String> =
            (1..BooksRepository.numOfChaptersPerBook[bookNumber - 1]).map { chapterFile(bookNumber, it) }

        private fun chapterFile(bookNumber: Int, chapter: Int): String {
            val name = BooksRepository.bookNamesEnglish[bookNumber - 1]
            return ChaptersRepository.getFileFormat(
                number = bookNumber,
                bookName = name,
                chapter = ChaptersRepository.padNumber(bookNameEnglish = name, chapterNum = chapter),
            )
        }

        private fun wholeChapters(files: List<String>): List<Segment> =
            files.map { Segment(file = it, firstVerse = 1, lastVerse = null) }
    }

    /** Verses [firstVerse]..[lastVerse] of one chapter file; to the end of the chapter when [lastVerse] is null. */
    private data class Segment(
        val file: String,
        val firstVerse: Int,
        val lastVerse: Int?,
    )

    /** The parasha's verses, and the first slot of its week, where a sequential rotation starts. */
    private class ParashaPlan(
        val segments: List<Segment>,
        val firstSlot: Long,
    )

    private companion object {
        const val MAX_ATTEMPTS = 6

        /** A longer verse does not fit a small widget; the nikud marks are not counted. */
        const val MAX_RANDOM_LETTERS = 150
        const val MAX_WEEKS_AHEAD = 3
        const val DAYS_BEFORE_SHABBAT = 6 // Sunday, the first day of the week

        const val ZERO_WIDTH_JOINER = "‍"

        /** 0x9E3779B97F4A7C15, spreads neighbouring slots over the whole range of seeds. */
        const val GOLDEN_GAMMA = -7046029254386353131L
    }
}
